/**
 * Next screen after Run AI: shows up to three ranked design options from intake.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
    CaretDown,
    CaretLeft,
    CaretRight,
    CheckCircle,
    CircleNotch,
    Cube,
    Info,
    Sparkle,
    Tree,
    WarningCircle,
} from '@phosphor-icons/react';
import { RoofModuleBoard } from './PreContractModuleStep';
import {
    getTreeCalculatorBandsForTree,
    TREE_CART_DEFAULT_HAUL,
    TREE_CART_DEFAULT_LOCATION,
    TREE_CART_DEFAULT_NO_OBST,
    TREE_CART_DEFAULT_STUMP,
} from '../../services/treeCartService';
import {
    averageIncludedMultiplier,
    roofIncludedMultiplierDetail,
} from '../lib/preContractPpwPricing';
import {
    countIncludedModules,
    estimateSelectedAnnualKwh,
} from '../lib/preContractModuleLayout';
import {
    generatePreContractAiDesigns,
    intakePatchFromAiDesign,
    loadPreContractAiOptionsCache,
    orderIncludedModuleIdsForFill,
    parsePreContractTargetKwh,
    persistPreContractAiOptionsCache,
    sanitizePreContractAiDesignOptionsForUsageTarget,
    type PreContractAiDesignOption,
    type PreContractAiProgress,
} from '../lib/preContractAiDesigns';
import type { PreContractIntakeForm } from '../lib/preContractTypes';
import { fillOrientationLabel, normalizeFillOrientations } from '../lib/preContractTypes';
import type {
    AiDesignOptionCardProps,
    PreContractAiDesignsStepProps,
    PreContractAiResultTab,
} from './PreContractAiDesignsStep.types';
import { PreContractAiDesignConfigButton } from './PreContractAiDesignConfigButton';
import { PreContractSelectedModulesPopover } from './PreContractSelectedModulesPopover';
import { PreContractAiConfigSubsection } from './PreContractAiConfigSubsection';

export type { PreContractAiDesignsStepProps };

const MIN_LOADING_MS = 350;

function formatKwh(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round(value).toLocaleString()} kWh`;
}

function formatKw(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '—';
    return `${value.toFixed(2)} kW`;
}

function formatCutCost(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${Math.round(value).toLocaleString()}`;
}

export const PreContractAiDesignsStep: React.FC<PreContractAiDesignsStepProps> = ({
    form,
    generationForm,
    patchForm,
    onBack,
    onApply,
    onNext,
    onOpenViewport,
    onPreviewAiOption,
    onPreviewOnViewport,
    viewportPreviewId = null,
    viewportPreviewLayouts,
    viewportPreviewRoofSelections,
    onPreviewToggleModule,
    onPreviewToggleRoof,
    viewportAvailable = true,
    actionsDisabled = false,
    embedded = false,
    solarPanelChild = false,
    onOpenSolarLayout,
}) => {
    // ----- Store -----
    const [layoutOptions, setLayoutOptions] = useState<PreContractAiDesignOption[]>([]);
    const [treeCutOptions, setTreeCutOptions] = useState<PreContractAiDesignOption[]>([]);
    const [resultTab, setResultTab] = useState<PreContractAiResultTab>('layouts');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [progress, setProgress] = useState<PreContractAiProgress>('layout');
    const [treeCutDefaultsOpen, setTreeCutDefaultsOpen] = useState(false);
    const pool = resultTab === 'trees' ? treeCutOptions : layoutOptions;
    const visibleOptions = pool.slice(0, 2);

    const applyOption = (option: PreContractAiDesignOption) => {
        if (!solarPanelChild) {
            patchForm(intakePatchFromAiDesign(option, form.siteTrees));
            onPreviewAiOption?.(option);
        }
    };

    // ----- Generate options from the intake snapshot -----
    useEffect(() => {
        let cancelled = false;

        void (async () => {
            const cached = loadPreContractAiOptionsCache();
            if (cached) {
                const intakeForGeneration = generationForm ?? form;
                const sanitizedLayouts = sanitizePreContractAiDesignOptionsForUsageTarget(
                    cached.layoutOptions,
                    intakeForGeneration,
                );
                const sanitizedTrees = sanitizePreContractAiDesignOptionsForUsageTarget(
                    cached.treeCutOptions,
                    intakeForGeneration,
                );
                const cachedTab =
                    cached.selectedTab === 'trees' && sanitizedTrees.length > 0
                        ? 'trees'
                        : 'layouts';
                const cachedPool =
                    cachedTab === 'trees' ? sanitizedTrees : sanitizedLayouts;
                const cachedSelected =
                    cachedPool.find((item) => item.id === cached.selectedId) ??
                    cachedPool[0] ??
                    null;
                if (cancelled) return;
                setLayoutOptions(sanitizedLayouts);
                setTreeCutOptions(sanitizedTrees);
                setResultTab(cachedTab);
                setSelectedId(cachedSelected?.id ?? null);
                setLoading(false);
                persistPreContractAiOptionsCache(
                    sanitizedLayouts,
                    sanitizedTrees,
                    cachedSelected?.id ?? null,
                    cachedTab,
                );
                if (cachedSelected && !solarPanelChild) {
                    patchForm(intakePatchFromAiDesign(cachedSelected, form.siteTrees));
                }
                return;
            }

            const started = Date.now();
            setLoading(true);
            setError(null);

            try {
                const intakeForGeneration = generationForm ?? form;
                const next = await generatePreContractAiDesigns(intakeForGeneration, {
                    onProgress: setProgress,
                });
                const wait = Math.max(0, MIN_LOADING_MS - (Date.now() - started));
                if (wait > 0) {
                    await new Promise((resolve) => window.setTimeout(resolve, wait));
                }
                if (cancelled) return;
                const first = next.layoutOptions[0] ?? null;
                setLayoutOptions(next.layoutOptions);
                setTreeCutOptions(next.treeCutOptions);
                setResultTab('layouts');
                setSelectedId(first?.id ?? null);
                persistPreContractAiOptionsCache(
                    next.layoutOptions,
                    next.treeCutOptions,
                    first?.id ?? null,
                    'layouts',
                );
                if (first && !solarPanelChild) {
                    patchForm(intakePatchFromAiDesign(first, form.siteTrees));
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Pre-contract AI designs failed', err);
                    setLayoutOptions([]);
                    setTreeCutOptions([]);
                    setSelectedId(null);
                    setError('Could not generate AI design options from this intake.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Snapshot the intake at the moment Run AI opens this screen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- Handlers -----
    const handleSelectDesign = (id: string) => {
        const option = visibleOptions.find((item) => item.id === id);
        if (!option) return;
        setSelectedId(id);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, id, resultTab);
        applyOption(option);
    };

    const handlePreviewViewport = (id: string) => {
        const option = visibleOptions.find((item) => item.id === id);
        if (!option) return;
        setSelectedId(id);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, id, resultTab);
        onPreviewOnViewport?.(option);
    };

    const handleResultTab = (tab: PreContractAiResultTab) => {
        if (tab === resultTab) return;
        if (tab !== 'trees') setTreeCutDefaultsOpen(false);
        const nextOptions = tab === 'trees' ? treeCutOptions : layoutOptions;
        const nextSelected =
            nextOptions.find((item) => item.id === selectedId)?.id ?? nextOptions[0]?.id ?? null;
        setResultTab(tab);
        setSelectedId(nextSelected);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, nextSelected, tab);
        const option = nextOptions.find((item) => item.id === nextSelected);
        if (option) applyOption(option);
    };

    const showLayoutsTab = () => handleResultTab('layouts');
    const showTreesTab = () => handleResultTab('trees');

    const handleApplyDesign = (id?: string) => {
        const targetId = id ?? selectedId;
        const option = visibleOptions.find((item) => item.id === targetId);
        if (!option) return;
        applyOption(option);
        persistPreContractAiOptionsCache(layoutOptions, treeCutOptions, option.id, resultTab);
        onApply(option);
    };

    const handleNext = () => {
        handleApplyDesign();
        onNext?.();
    };

    const selected = visibleOptions.find((item) => item.id === selectedId) ?? null;
    const actionsReady = !actionsDisabled && !loading && selected != null;
    const selectedFills = normalizeFillOrientations(form.fillOrientations);
    const fillList = selectedFills.map((item) => fillOrientationLabel(item).toLowerCase()).join(', ');
    const progressCopy =
        progress === 'maxfill'
            ? {
                  title: 'Preparing module layout…',
                  detail: `Running max-fill for ${fillList || 'your selected orientation'}.`,
              }
            : progress === 'layout'
              ? {
                    title: 'Building recommended design…',
                    detail:
                        'Choosing the best module set from your configuration, roofs, usage, and offset preference.',
                }
              : progress === 'gemini'
                ? {
                      title: 'Asking AI for the best layout…',
                      detail: 'Gemini is choosing module sets from the max-fill layouts.',
                  }
                : progress === 'treecut'
                  ? {
                        title: 'Applying tree cutting…',
                        detail:
                            'Ghosting the highest-multiplier tree and re-measuring layouts, plus estimated cut cost.',
                    }
                  : {
                        title: 'Measuring production in OneDraw…',
                        detail: 'Placing the recommended layout in the 3D viewer and reading kWh Actual.',
                    };
    const targetKwh = parsePreContractTargetKwh(form.annualUsageKwh);
    const targetLabel = targetKwh > 0 ? `${Math.round(targetKwh).toLocaleString()} kWh/yr` : null;
    const headTitle =
        resultTab === 'trees' ? 'Best layout with tree cutting' : 'Choose your top layout';
    const headMeta =
        !loading && visibleOptions.length > 0
            ? `${visibleOptions.length} ranked design${visibleOptions.length === 1 ? '' : 's'}`
            : null;
    const showTreeTab = !loading && treeCutOptions.length > 0;

    // ----- Render -----
    const designsContent = (
        <div
            id="pci-section-ai"
            className={`pci-ai-designs${embedded ? ' pci-ai-designs--embedded' : ''}${
                solarPanelChild ? ' pci-ai-designs--solar-panel' : ''
            }${visibleOptions.length === 2 ? ' pci-ai-designs--duo' : ''}`}
        >
            {!solarPanelChild ? (
                <header className="pci-ai-designs__head">
                    <div className="pci-ai-designs__head-copy">
                        <h2 className="pci-ai-designs__head-title">{headTitle}</h2>
                        {headMeta || targetLabel ? (
                            <p className="pci-ai-designs__head-meta">
                                {headMeta}
                                {headMeta && targetLabel ? ' · ' : null}
                                {targetLabel ? (
                                    <>
                                        Target <strong>{targetLabel}</strong>
                                    </>
                                ) : null}
                            </p>
                        ) : null}
                    </div>
                    <div className="pci-ai-designs__head-actions">
                        {onOpenSolarLayout ? (
                            <PreContractAiDesignConfigButton onClick={onOpenSolarLayout} />
                        ) : null}
                    </div>
                </header>
            ) : null}

            {showTreeTab ? (
                <div className="pci-ai-designs__tabs" role="tablist" aria-label="AI result views">
                    <button
                        type="button"
                        role="tab"
                        className={`pci-ai-designs__tab${
                            resultTab === 'layouts' ? ' pci-ai-designs__tab--on' : ''
                        }`}
                        aria-selected={resultTab === 'layouts'}
                        onClick={showLayoutsTab}
                    >
                        Layouts
                    </button>
                    <div className="pci-ai-designs__tab-with-info">
                        <button
                            type="button"
                            role="tab"
                            className={`pci-ai-designs__tab${
                                resultTab === 'trees' ? ' pci-ai-designs__tab--on' : ''
                            }`}
                            aria-selected={resultTab === 'trees'}
                            onClick={showTreesTab}
                        >
                            <Tree size={14} weight="fill" />
                            Tree Cutting
                        </button>
                        <TreeCutDefaultsInfo
                            open={treeCutDefaultsOpen}
                            onOpenChange={(open) => {
                                setTreeCutDefaultsOpen(open);
                                if (open && resultTab !== 'trees') showTreesTab();
                            }}
                            form={form}
                            treeCutOptions={treeCutOptions}
                        />
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div className="pci-ai-designs__loading" role="status">
                    <CircleNotch className="pci-ai-designs__spinner" size={28} weight="bold" />
                    <strong>{progressCopy.title}</strong>
                    <span>
                        {progressCopy.detail}
                        {targetLabel ? ` Target ${targetLabel}.` : ''}
                    </span>
                </div>
            ) : null}

            {!loading && error ? (
                <div className="pci-ai-designs__empty" role="alert">
                    <WarningCircle size={20} weight="fill" />
                    <p>{error}</p>
                </div>
            ) : null}

            {!loading && !error && visibleOptions.length === 0 ? (
                <div className="pci-ai-designs__empty" role="status">
                    <WarningCircle size={20} weight="fill" />
                    <p>
                        {resultTab === 'trees'
                            ? 'No high-multiplier trees to cut for this site.'
                            : 'No design options could be built from the current module layout.'}
                    </p>
                </div>
            ) : null}

            {!loading && visibleOptions.length > 0 ? (
                <div className="pci-ai-designs__grid">
                    {visibleOptions.map((option, index) => (
                        <AiDesignOptionCard
                            key={option.id}
                            option={option}
                            optionNumber={index + 1}
                            rankLabel={index === 0 ? 'Top pick' : 'Runner-up'}
                            selected={option.id === selectedId}
                            onSelect={handleSelectDesign}
                            onPreviewViewport={
                                onPreviewOnViewport ? handlePreviewViewport : undefined
                            }
                            viewportPreviewActive={option.id === viewportPreviewId}
                            previewLayouts={
                                option.id === viewportPreviewId ? viewportPreviewLayouts : undefined
                            }
                            onPreviewToggleModule={
                                option.id === viewportPreviewId ? onPreviewToggleModule : undefined
                            }
                            onPreviewToggleRoof={
                                option.id === viewportPreviewId ? onPreviewToggleRoof : undefined
                            }
                            viewportAvailable={viewportAvailable}
                            targetKwh={targetKwh}
                            moduleWattage={form.moduleTypeWattage}
                            includeGreyPanels={form.includeGreyPanels}
                            includePurplePanels={form.includePurplePanels}
                            fillOrderIds={orderIncludedModuleIdsForFill(
                                form,
                                option.layouts,
                                option.roofSelections,
                            )}
                            escalator={form.targetEscalator}
                            rate={form.targetMinPpaRate}
                            form={form}
                            aiConfigTone={solarPanelChild ? 'dark' : 'light'}
                        />
                    ))}
                </div>
            ) : null}

            {!embedded ? (
            <div className="pci-board-fab-dock" aria-label="Board actions">
                <div className="pci-board-fab-dock__start">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onBack}
                        title="Back"
                        aria-label="Back"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </button>
                </div>
                <div className="pci-board-fab-dock__main" aria-hidden />
                <div className="pci-board-fab-dock__end">
                    <button
                        type="button"
                        className="pci-board-fab__btn pci-board-fab__btn--primary"
                        disabled={!actionsReady}
                        onClick={handleNext}
                        title="Next"
                        aria-label="Next"
                    >
                        <CaretRight size={20} weight="bold" />
                    </button>
                </div>
            </div>
            ) : null}
        </div>
    );

    if (solarPanelChild) {
        return (
            <div className="pci-module-panel__scroll pci-module-panel__scroll--designs">
                {designsContent}
            </div>
        );
    }

    return designsContent;
};

function cutTreeCalculatorLabels(
    form: PreContractIntakeForm,
    treeCutOptions: PreContractAiDesignOption[],
): { dbh: string; height: string } {
    const option = treeCutOptions[0];
    const treeId = option?.treeCut?.treeIds[0];
    const tree = treeId ? form.siteTrees.find((item) => item.id === treeId) : undefined;
    const computed = tree ? getTreeCalculatorBandsForTree(tree) : { dbhRange: null, heightRange: null };
    const dbh = option?.treeCut?.dbhRange || computed.dbhRange;
    const height = option?.treeCut?.heightRange || computed.heightRange;
    return {
        dbh: dbh ?? 'From cut tree',
        height: height ? `${height} ft` : 'From cut tree',
    };
}

function TreeCutDefaultsInfo({
    open,
    onOpenChange,
    form,
    treeCutOptions,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    form: PreContractIntakeForm;
    treeCutOptions: PreContractAiDesignOption[];
}) {
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const onPointer = (event: MouseEvent) => {
            if (!wrapRef.current?.contains(event.target as Node)) onOpenChange(false);
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onOpenChange(false);
        };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onOpenChange]);

    const bands = cutTreeCalculatorLabels(form, treeCutOptions);
    const rows: Array<{ label: string; value: string }> = [
        { label: 'Location', value: TREE_CART_DEFAULT_LOCATION },
        { label: 'DBH', value: bands.dbh },
        { label: 'Height', value: bands.height },
        { label: 'Stump', value: TREE_CART_DEFAULT_STUMP },
        { label: 'Haul wood', value: TREE_CART_DEFAULT_HAUL },
        { label: 'Obstructions', value: String(TREE_CART_DEFAULT_NO_OBST) },
    ];

    return (
        <div className="pci-ai-designs__defaults" ref={wrapRef}>
            <button
                type="button"
                className={`pci-ai-designs__info-btn${open ? ' pci-ai-designs__info-btn--on' : ''}`}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label="Tree cutting default options"
                title="Tree cutting default options"
                onClick={() => onOpenChange(!open)}
            >
                <Info size={13} weight="bold" />
            </button>
            {open ? (
                <div
                    className="pci-ai-designs__defaults-pop"
                    role="dialog"
                    aria-label="Tree cutting default options"
                >
                    <p>Estimated cut cost uses these Tree Calculator defaults:</p>
                    <dl>
                        {rows.map((row) => (
                            <div key={row.label}>
                                <dt>{row.label}</dt>
                                <dd>{row.value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ) : null}
        </div>
    );
}

const AiDesignOptionCard: React.FC<AiDesignOptionCardProps> = ({
    option,
    optionNumber,
    rankLabel,
    selected,
    onSelect,
    onPreviewViewport,
    viewportPreviewActive = false,
    previewLayouts,
    onPreviewToggleModule,
    onPreviewToggleRoof,
    viewportAvailable,
    targetKwh,
    moduleWattage = null,
    fillOrderIds,
    includeGreyPanels,
    includePurplePanels,
    escalator = null,
    rate = null,
    form = null,
    aiConfigTone = 'light',
}) => {
    const [modulesOpen, setModulesOpen] = useState(false);
    const [layoutExpanded, setLayoutExpanded] = useState(false);

    useEffect(() => {
        if (!selected) setLayoutExpanded(false);
    }, [selected]);

    useEffect(() => {
        if (!viewportPreviewActive) setLayoutExpanded(false);
    }, [viewportPreviewActive]);

    const handleSelect = () => onSelect(option.id);
    const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleSelect();
    };
    const handleToggleLayout = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setLayoutExpanded((expanded) => !expanded);
    };
    const handlePreviewViewport = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onPreviewViewport?.(option.id);
    };
    const previewActiveLayouts =
        viewportPreviewActive && previewLayouts?.length ? previewLayouts : null;

    /** While the design is live on the viewport, every stat follows the panel toggles. */
    const liveStats = React.useMemo(() => {
        if (!previewActiveLayouts) return null;
        const roofSelections = previewActiveLayouts.map((layout) =>
            layout.modules.some((cell) => cell?.included),
        );
        const { count, averageSolarAccess } = countIncludedModules(
            previewActiveLayouts,
            roofSelections,
        );
        const production = estimateSelectedAnnualKwh({
            layouts: previewActiveLayouts,
            roofSelections,
            moduleWattage,
        });
        const multiplier = averageIncludedMultiplier(previewActiveLayouts, roofSelections);
        const systemKw =
            moduleWattage != null && moduleWattage > 0 && count > 0
                ? (count * moduleWattage) / 1000
                : option.systemKw;
        const annualKwh = production.kwh ?? option.annualKwh;
        return {
            moduleCount: count,
            averageSolarAccess: averageSolarAccess > 0 ? averageSolarAccess : option.averageSolarAccess,
            systemKw,
            annualKwh,
            multiplier: multiplier ?? option.multiplier,
            offsetPercent:
                targetKwh > 0 && annualKwh > 0 ? Math.round((annualKwh / targetKwh) * 100) : null,
            roofCount: roofSelections.filter(Boolean).length,
        };
    }, [moduleWattage, option, previewActiveLayouts, targetKwh]);

    const offsetPercent = liveStats ? liveStats.offsetPercent : option.offsetPercent;
    const offsetLabel = offsetPercent != null ? `${offsetPercent}%` : '—';
    const offsetTone =
        offsetPercent == null
            ? 'none'
            : offsetPercent >= 100
              ? 'over'
              : offsetPercent >= 92
                ? 'near'
                : 'under';
    const targetLabel = targetKwh > 0 ? formatKwh(targetKwh) : '—';
    const cut = option.treeCut;
    const cutTreesLabel = cut?.treeLabels.length ? cut.treeLabels.join(', ') : 'tree';
    const cutGain =
        cut?.annualKwhGain != null && cut.annualKwhGain !== 0
            ? `${cut.annualKwhGain > 0 ? '+' : ''}${Math.round(cut.annualKwhGain).toLocaleString()} kWh vs layout`
            : null;
    const layoutSource = previewActiveLayouts ?? option.layouts;
    const displayLayouts = [...layoutSource].sort((a, b) => a.roofIndex - b.roofIndex);
    const roofCount = liveStats ? liveStats.roofCount : option.roofLabels.length;
    const displayProductionKwh = liveStats ? liveStats.annualKwh : option.annualKwh;
    const isBest = option.rank === 1;
    const layoutInteractive =
        viewportPreviewActive &&
        Boolean(onPreviewToggleModule && onPreviewToggleRoof);

    type SpecItem = {
        label: string;
        value: string;
        tone?: 'over' | 'near' | 'under' | 'none';
    };

    const specSystemKw = liveStats ? liveStats.systemKw : option.systemKw;
    const specModuleCount = liveStats ? liveStats.moduleCount : option.moduleCount;
    const specMultiplier = liveStats ? liveStats.multiplier : option.multiplier;
    const specAverageSolarAccess = liveStats
        ? liveStats.averageSolarAccess
        : option.averageSolarAccess;

    const metricSpecs: SpecItem[] = [
        { label: 'System', value: formatKw(specSystemKw) },
        { label: 'Modules', value: specModuleCount > 0 ? String(specModuleCount) : '—' },
        ...(specMultiplier != null
            ? [{ label: 'Mult', value: specMultiplier.toFixed(3) }]
            : []),
        { label: 'Avg SA', value: `${specAverageSolarAccess.toFixed(0)}%` },
        { label: 'Roofs', value: roofCount > 0 ? String(roofCount) : '—' },
    ];
    const offsetBarPercent =
        offsetPercent != null ? Math.min(Math.max(offsetPercent, 0), 110) / 1.1 : 0;
    const offsetTargetMarkPercent = 100 / 1.1;

    return (
        <article
            className={`pci-ai-design-card${selected ? ' pci-ai-design-card--selected' : ''}${
                isBest ? ' pci-ai-design-card--best' : ''
            }${viewportPreviewActive ? ' pci-ai-design-card--viewport-live' : ''}`}
            aria-pressed={selected}
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleCardKeyDown}
        >
            {viewportPreviewActive ? (
                <div className="pci-ai-design-card__live-ribbon" aria-live="polite">
                    <span className="pci-ai-design-card__live-dot" aria-hidden />
                    <span>Live on viewport</span>
                    <span className="pci-ai-design-card__live-hint">
                        Tap modules to adjust selection
                    </span>
                </div>
            ) : null}
            <header className="pci-ai-design-card__top">
                <div className="pci-ai-design-card__top-main">
                    <span className="pci-ai-design-card__rank">{optionNumber}</span>
                    <div className="pci-ai-design-card__badges">
                        {isBest ? (
                            <span className="pci-ai-design-card__badge pci-ai-design-card__badge--best">
                                <Sparkle size={10} weight="fill" aria-hidden />
                                {rankLabel ?? 'Top pick'}
                            </span>
                        ) : (
                            <span className="pci-ai-design-card__badge pci-ai-design-card__badge--alt">
                                {rankLabel ?? `Option ${optionNumber}`}
                            </span>
                        )}
                        {option.fillOrientation ? (
                            <span className="pci-ai-design-card__badge pci-ai-design-card__badge--orient">
                                {fillOrientationLabel(option.fillOrientation)}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="pci-ai-design-card__top-actions">
                    <PreContractSelectedModulesPopover
                        layouts={layoutSource}
                        allModulesLayouts={option.fullModuleLayouts}
                        roofSelections={option.roofSelections}
                        fillOrderIds={fillOrderIds}
                        targetKwh={targetKwh}
                        actualProductionKwh={displayProductionKwh}
                        escalator={escalator}
                        rate={rate}
                        form={form}
                        open={modulesOpen}
                        onOpenChange={setModulesOpen}
                        contextLabel={option.title}
                        variant="card"
                    />
                </div>
            </header>

            <div className="pci-ai-design-card__stats-band">
                <div className="pci-ai-design-card__stats-hero">
                    <div className="pci-ai-design-card__stats-primary">
                        <span className="pci-ai-design-card__stats-kicker">Production</span>
                        <strong className="pci-ai-design-card__stats-kwh">
                            {formatKwh(displayProductionKwh)}
                        </strong>
                        {targetKwh > 0 ? (
                            <span className="pci-ai-design-card__stats-target">
                                Target {targetLabel}
                            </span>
                        ) : null}
                    </div>
                    {offsetPercent != null ? (
                        <div
                            className={`pci-ai-design-card__offset-chip pci-ai-design-card__offset-chip--${offsetTone}`}
                        >
                            <span className="pci-ai-design-card__offset-chip-label">Offset</span>
                            <strong className="pci-ai-design-card__offset-chip-value">
                                {offsetLabel}
                            </strong>
                        </div>
                    ) : null}
                </div>

                {targetKwh > 0 && offsetPercent != null ? (
                    <div className="pci-ai-design-card__offset-track" aria-hidden>
                        <span
                            className={`pci-ai-design-card__offset-fill pci-ai-design-card__offset-fill--${offsetTone}`}
                            style={{ width: `${offsetBarPercent}%` }}
                        />
                        <span
                            className="pci-ai-design-card__offset-mark"
                            style={{ left: `${offsetTargetMarkPercent}%` }}
                            title="100% target"
                        />
                    </div>
                ) : null}

                <dl className="pci-ai-design-card__metrics" aria-label="System specs">
                    {metricSpecs.map((spec) => (
                        <div key={spec.label} className="pci-ai-design-card__metric">
                            <dt>{spec.label}</dt>
                            <dd
                                className={
                                    spec.tone && spec.tone !== 'none'
                                        ? `pci-ai-design-card__metric-value--${spec.tone}`
                                        : undefined
                                }
                            >
                                {spec.value}
                            </dd>
                        </div>
                    ))}
                </dl>

                {onPreviewViewport ? (
                    <button
                        type="button"
                        className={`pci-ai-design-card__viewport-preview${
                            viewportPreviewActive ? ' pci-ai-design-card__viewport-preview--on' : ''
                        }`}
                        disabled={!viewportAvailable}
                        aria-pressed={viewportPreviewActive}
                        onClick={handlePreviewViewport}
                    >
                        {viewportPreviewActive ? (
                            <>
                                <CheckCircle size={15} weight="fill" aria-hidden />
                                Stop viewport preview
                            </>
                        ) : (
                            <>
                                <Cube size={15} weight="duotone" aria-hidden />
                                Preview on viewport
                            </>
                        )}
                    </button>
                ) : null}
            </div>

            <p className="pci-ai-design-card__reason">{option.reason}</p>

            {cut ? (
                <div className="pci-ai-design-card__cut">
                    <div className="pci-ai-design-card__cut-main">
                        <span>Cut {cutTreesLabel}</span>
                        <strong>{formatCutCost(cut.cutCost)}</strong>
                    </div>
                    {cutGain ? <span className="pci-ai-design-card__cut-gain">{cutGain}</span> : null}
                </div>
            ) : null}

            <PreContractAiConfigSubsection
                layouts={layoutSource}
                includeGreyPanels={includeGreyPanels}
                includePurplePanels={includePurplePanels}
                panelId={`pci-ai-design-ai-config-${option.id}`}
                tone={aiConfigTone}
            />

            <section
                className={`pci-ai-design-card__layout${
                    layoutExpanded ? '' : ' pci-ai-design-card__layout--collapsed'
                }`}
            >
                <button
                    type="button"
                    className="pci-ai-design-card__layout-trigger"
                    aria-expanded={layoutExpanded}
                    aria-controls={`pci-ai-design-layout-${option.id}`}
                    onClick={handleToggleLayout}
                >
                    <span className="pci-ai-design-card__layout-trigger-label">Layout</span>
                    {roofCount > 0 ? (
                        <span className="pci-ai-design-card__layout-trigger-meta">
                            {option.roofLabels.join(' · ')}
                        </span>
                    ) : null}
                    <CaretDown
                        className="pci-ai-design-card__layout-caret"
                        size={12}
                        weight="bold"
                        aria-hidden
                    />
                </button>
                {layoutExpanded ? (
                    <div
                        id={`pci-ai-design-layout-${option.id}`}
                        className="pci-ai-design-card__layout-panel"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                    >
                        <div className="pci-module-panel__scroll pci-ai-design-card__module-scroll">
                            <div className="pci-module-layout-grid">
                                {displayLayouts.map((layout) => {
                                    const roofActive = layout.modules.some((cell) =>
                                        Boolean(cell?.included),
                                    );
                                    const inactive = !roofActive;
                                    const multiplierDetail = inactive
                                        ? {
                                              multiplier: null,
                                              meanSolarAccess: null,
                                              includedCount: 0,
                                          }
                                        : roofIncludedMultiplierDetail(layout);
                                    return (
                                        <RoofModuleBoard
                                            key={`${option.id}-${layout.roofIndex}`}
                                            layout={layout}
                                            multiplierDetail={multiplierDetail}
                                            inactive={inactive}
                                            includeGreyPanels={includeGreyPanels}
                                            includePurplePanels={includePurplePanels}
                                            compact
                                            readOnly={!layoutInteractive}
                                            onToggleRoof={(event) => {
                                                event.stopPropagation();
                                                onPreviewToggleRoof?.(
                                                    layout.roofIndex,
                                                    !roofActive,
                                                );
                                            }}
                                            onToggle={(moduleIndex) =>
                                                onPreviewToggleModule?.(
                                                    layout.roofIndex,
                                                    moduleIndex,
                                                )
                                            }
                                            onHover={() => undefined}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : null}
            </section>
        </article>
    );
};

export default PreContractAiDesignsStep;
